import { useEffect, useRef, useState } from "react";
import { api, API } from "../../lib/api";
import { Plus, Pencil, Trash2, KeyRound, Upload, FileText, Download, Eye, RefreshCw, X, Folder, History, ArrowLeft } from "lucide-react";
import FillableFormsPanel from "./FillableFormsPanel";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; }
}
function humanSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function ClientForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial || { username: "", access_code: "", name: "", email: "", active: true, notes: "" });
  const isNew = !initial;
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 space-y-3">
      <h4 className="font-display font-bold text-lg text-[#1A1A1A]">{isNew ? "Nuovo cliente" : "Modifica cliente"}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Nome Utente *</label>
          <input data-testid="cli-form-username" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" placeholder="mario.rossi" />
        </div>
        {isNew && (
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Codice di Accesso *</label>
            <input data-testid="cli-form-code" type="text" value={f.access_code} onChange={(e) => setF({ ...f, access_code: e.target.value })} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm font-mono" placeholder="min 6 caratteri" />
          </div>
        )}
        <div className={isNew ? "md:col-span-2" : ""}>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Nome / Ragione Sociale</label>
          <input data-testid="cli-form-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Email (opzionale)</label>
          <input data-testid="cli-form-email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Stato</label>
          <select value={f.active ? "true" : "false"} onChange={(e) => setF({ ...f, active: e.target.value === "true" })} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm">
            <option value="true">Attivo</option>
            <option value="false">Disattivato</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Note interne</label>
          <textarea rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm rounded-full border border-[#E5E7EB] hover:bg-[#F9FAFB]">Annulla</button>
        <button data-testid="cli-form-save" onClick={() => onSave(f)} className="px-5 py-2 text-sm rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white">Salva</button>
      </div>
    </div>
  );
}

function DocsPanel({ client, onBack }) {
  const [docs, setDocs] = useState([]);
  const [cats, setCats] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [defaultCat, setDefaultCat] = useState("Altri Documenti");
  const replaceInputRef = useRef(null);
  const [replaceDoc, setReplaceDoc] = useState(null);

  const load = () => {
    api.get(`/admin/clients/${client.id}/documents`).then((r) => setDocs(r.data || []));
  };
  useEffect(() => {
    load();
    api.get("/admin/document-categories").then((r) => setCats(r.data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);
  const loadLogs = () => api.get(`/admin/clients/${client.id}/access-logs`).then((r) => setLogs(r.data || []));

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    const token = localStorage.getItem("bg_token");
    const form = new FormData();
    Array.from(files).forEach((f) => form.append("files", f));
    form.append("category", defaultCat);
    setUploading(true);
    try {
      const res = await fetch(`${API}/admin/clients/${client.id}/documents/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload fallito (${res.status})`);
      }
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const updateDoc = async (doc, patch) => {
    await api.put(`/admin/documents/${doc.id}`, patch);
    setEditing(null);
    load();
  };
  const deleteDoc = async (doc) => {
    if (!confirm(`Eliminare "${doc.title}"?`)) return;
    await api.delete(`/admin/documents/${doc.id}`);
    load();
  };
  const downloadAdmin = async (doc) => {
    const token = localStorage.getItem("bg_token");
    const res = await fetch(`${API}/admin/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { alert("Download fallito"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = doc.filename; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const onReplace = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replaceDoc) return;
    const token = localStorage.getItem("bg_token");
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${API}/admin/documents/${replaceDoc.id}/replace`, { method: "PUT", headers: { Authorization: `Bearer ${token}` }, body: form });
    if (!res.ok) { const err = await res.json().catch(() => ({})); alert(err.detail || "Sostituzione fallita"); }
    setReplaceDoc(null);
    e.target.value = "";
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button data-testid="docs-back" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#007A3D]">
            <ArrowLeft className="w-4 h-4" /> Indietro
          </button>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#007A3D]">Documenti</div>
            <div className="font-display font-bold text-lg text-[#1A1A1A]">{client.name} <span className="text-[#6B7280] font-normal">@{client.username}</span></div>
          </div>
        </div>
        <button data-testid="toggle-logs" onClick={() => { setShowLogs((v) => !v); if (!showLogs) loadLogs(); }} className="inline-flex items-center gap-1.5 text-sm text-[#1A1A1A] hover:text-[#007A3D] border border-[#E5E7EB] rounded-full px-4 py-2">
          <History className="w-4 h-4" /> {showLogs ? "Nascondi log" : "Log accessi"}
        </button>
      </div>

      {/* Drop zone */}
      <div
        data-testid="docs-dropzone"
        onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
        className={`relative rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${dragOver ? "border-[#00A651] bg-[#EAF8F0]" : "border-[#E5E7EB] bg-[#F9FAFB]"}`}
      >
        <Upload className="w-7 h-7 mx-auto mb-2 text-[#007A3D]" />
        <div className="text-sm font-semibold text-[#1A1A1A]">Trascina i file qui</div>
        <div className="text-xs text-[#6B7280] mt-1">oppure clicca per selezionarli · PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, ZIP · max 25 MB</div>
        <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
          <select value={defaultCat} onChange={(e) => setDefaultCat(e.target.value)} className="rounded-full border border-[#E5E7EB] px-4 py-2 text-xs bg-white">
            {cats.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 text-sm font-medium bg-[#007A3D] hover:bg-[#00A651] text-white rounded-full px-5 py-2 cursor-pointer">
            <Upload className="w-4 h-4" /> Seleziona file
            <input data-testid="docs-file-input" type="file" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
          </label>
        </div>
        {uploading && <div className="absolute inset-0 rounded-2xl bg-white/70 backdrop-blur-sm flex items-center justify-center text-sm text-[#007A3D] font-medium"><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Caricamento in corso…</div>}
      </div>

      {/* Hidden file input for replace */}
      <input ref={replaceInputRef} type="file" className="hidden" onChange={onReplace} />

      {/* Docs list */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {docs.length === 0 ? (
          <div className="p-10 text-center text-[#6B7280] text-sm">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Nessun documento caricato.
          </div>
        ) : (
          <ul className="divide-y divide-[#F3F4F6]">
            {docs.map((d) => (
              <li key={d.id} data-testid={`admin-doc-${d.id}`} className="px-5 py-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  {editing?.id === d.id ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" placeholder="Titolo" />
                      <select value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm">
                        {cats.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
                      </select>
                      <textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="md:col-span-2 rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" placeholder="Descrizione" />
                    </div>
                  ) : (
                    <>
                      <div className="font-semibold text-[#1A1A1A] text-sm truncate">{d.title}</div>
                      <div className="text-xs text-[#6B7280] mt-0.5 truncate">{d.category} · {humanSize(d.size)} · car. {fmtDate(d.uploaded_at)} · agg. {fmtDate(d.updated_at)} · {d.filename}</div>
                      {d.description && <div className="text-xs text-[#6B7280] mt-1 line-clamp-1">{d.description}</div>}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {editing?.id === d.id ? (
                    <>
                      <button onClick={() => updateDoc(d, { title: editing.title, category: editing.category, description: editing.description })} className="text-xs px-3 py-1.5 rounded-full bg-[#007A3D] text-white">Salva</button>
                      <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 rounded-full border border-[#E5E7EB]">Annulla</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => downloadAdmin(d)} title="Scarica" className="p-2 rounded-full hover:bg-[#F9FAFB] text-[#1A1A1A]"><Download className="w-4 h-4" /></button>
                      <button onClick={() => { setReplaceDoc(d); replaceInputRef.current?.click(); }} title="Sostituisci file" className="p-2 rounded-full hover:bg-[#F9FAFB] text-[#1A1A1A]"><RefreshCw className="w-4 h-4" /></button>
                      <button onClick={() => setEditing({ id: d.id, title: d.title, category: d.category, description: d.description || "" })} title="Modifica info" className="p-2 rounded-full hover:bg-[#F9FAFB] text-[#1A1A1A]"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => deleteDoc(d)} title="Elimina" className="p-2 rounded-full hover:bg-[#FEE2E2] text-[#B91C1C]"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Access Logs */}
      {showLogs && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
          <div className="font-display font-bold text-sm uppercase tracking-[0.15em] text-[#007A3D] mb-3">Log accessi</div>
          {logs.length === 0 ? (
            <div className="text-sm text-[#6B7280]">Nessun accesso registrato.</div>
          ) : (
            <ul className="text-xs text-[#1A1A1A] space-y-1 max-h-72 overflow-y-auto font-mono">
              {logs.map((l) => (
                <li key={l.id} className={l.action === "login_success" ? "text-[#007A3D]" : l.action === "login_fail" ? "text-[#B91C1C]" : ""}>
                  {fmtDate(l.ts)} · {l.action} · ip {l.ip || "—"}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Fillable forms */}
      <FillableFormsPanel client={client} />
    </div>
  );
}

function CategoryManager() {
  const [cats, setCats] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/document-categories").then((r) => setCats(r.data || []));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) return;
    try { await api.post("/admin/document-categories", { name: newName.trim() }); setNewName(""); load(); }
    catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };
  const save = async (c) => {
    try { await api.put(`/admin/document-categories/${c.slug}`, { name: editing.name }); setEditing(null); load(); }
    catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };
  const del = async (c) => {
    if (!confirm(`Eliminare "${c.name}"?`)) return;
    try { await api.delete(`/admin/document-categories/${c.slug}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
      <div className="flex items-center gap-2 mb-4">
        <Folder className="w-4 h-4 text-[#007A3D]" />
        <h4 className="font-display font-bold text-base text-[#1A1A1A]">Categorie documenti</h4>
      </div>
      <div className="flex gap-2 mb-4">
        <input data-testid="new-cat-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nuova categoria" className="flex-1 rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" />
        <button data-testid="new-cat-add" onClick={add} className="inline-flex items-center gap-1 px-4 py-2 text-sm rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white"><Plus className="w-4 h-4" /> Aggiungi</button>
      </div>
      <ul className="divide-y divide-[#F3F4F6]">
        {cats.map((c) => (
          <li key={c.slug} className="py-2 flex items-center gap-2">
            {editing?.slug === c.slug ? (
              <>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="flex-1 rounded-xl border border-[#E5E7EB] px-3 py-1.5 text-sm" />
                <button onClick={() => save(c)} className="text-xs px-3 py-1.5 rounded-full bg-[#007A3D] text-white">Salva</button>
                <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 rounded-full border border-[#E5E7EB]">Annulla</button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm text-[#1A1A1A]">{c.name}</span>
                <button onClick={() => setEditing({ slug: c.slug, name: c.name })} className="p-1.5 rounded-full hover:bg-[#F9FAFB]"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => del(c)} className="p-1.5 rounded-full hover:bg-[#FEE2E2] text-[#B91C1C]"><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ClientsManager() {
  const [clients, setClients] = useState([]);
  const [view, setView] = useState("list"); // list | new | edit | docs | categories
  const [selected, setSelected] = useState(null);
  const [resetingFor, setResetingFor] = useState(null);
  const [newCode, setNewCode] = useState("");

  const load = () => api.get("/admin/clients").then((r) => setClients(r.data || []));
  useEffect(() => { load(); }, []);

  const create = async (f) => {
    try {
      await api.post("/admin/clients", f);
      setView("list"); load();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };
  const update = async (f) => {
    try {
      await api.put(`/admin/clients/${selected.id}`, { name: f.name, email: f.email, active: f.active, notes: f.notes, username: f.username });
      setSelected(null); setView("list"); load();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };
  const del = async (c) => {
    if (!confirm(`Eliminare il cliente "${c.name || c.username}" e TUTTI i suoi documenti? Operazione irreversibile.`)) return;
    await api.delete(`/admin/clients/${c.id}`);
    load();
  };
  const submitReset = async () => {
    if (newCode.length < 6) { alert("Min 6 caratteri"); return; }
    try { await api.post(`/admin/clients/${resetingFor.id}/reset-code`, { access_code: newCode }); setResetingFor(null); setNewCode(""); alert("Codice aggiornato"); }
    catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  if (view === "docs" && selected) {
    return <DocsPanel client={selected} onBack={() => { setSelected(null); setView("list"); load(); }} />;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-xl text-[#1A1A1A]">Gestione clienti</h3>
          <p className="text-sm text-[#6B7280]">Crea i clienti, assegna le credenziali, gestisci i loro documenti.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="show-categories" onClick={() => setView(view === "categories" ? "list" : "categories")} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-full border border-[#E5E7EB] hover:border-[#00A651]"><Folder className="w-4 h-4" /> Categorie</button>
          <button data-testid="new-client" onClick={() => { setSelected(null); setView("new"); }} className="inline-flex items-center gap-1.5 px-5 py-2 text-sm rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white"><Plus className="w-4 h-4" /> Nuovo cliente</button>
        </div>
      </div>

      {view === "categories" && <CategoryManager />}
      {view === "new" && <ClientForm onSave={create} onCancel={() => setView("list")} />}
      {view === "edit" && selected && <ClientForm initial={selected} onSave={update} onCancel={() => { setView("list"); setSelected(null); }} />}

      {/* List */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-[0.1em]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Nome utente</th>
                <th className="text-left px-5 py-3 font-semibold">Cliente</th>
                <th className="text-left px-5 py-3 font-semibold">Stato</th>
                <th className="text-left px-5 py-3 font-semibold">Ultimo accesso</th>
                <th className="text-right px-5 py-3 font-semibold">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {clients.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-[#6B7280]">Nessun cliente. Crea il primo.</td></tr>
              )}
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className="hover:bg-[#F9FAFB]">
                  <td className="px-5 py-3 font-mono text-xs text-[#1A1A1A]">@{c.username}</td>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-[#1A1A1A]">{c.name || "—"}</div>
                    {c.email && <div className="text-xs text-[#6B7280]">{c.email}</div>}
                  </td>
                  <td className="px-5 py-3">
                    {c.active ? (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#EAF8F0] text-[#007A3D]">Attivo</span>
                    ) : (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#FEF2F2] text-[#B91C1C]">Disattivato</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-[#6B7280]">{c.last_login_at ? fmtDate(c.last_login_at) : "mai"}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`open-docs-${c.id}`} onClick={() => { setSelected(c); setView("docs"); }} title="Documenti" className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white"><FileText className="w-3.5 h-3.5" /> Documenti</button>
                      <button onClick={() => setResetingFor(c)} title="Reimposta codice" className="p-2 rounded-full hover:bg-[#F9FAFB]"><KeyRound className="w-4 h-4" /></button>
                      <button onClick={() => { setSelected(c); setView("edit"); }} title="Modifica" className="p-2 rounded-full hover:bg-[#F9FAFB]"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(c)} title="Elimina" className="p-2 rounded-full hover:bg-[#FEE2E2] text-[#B91C1C]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset code modal */}
      {resetingFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setResetingFor(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl border border-[#E5E7EB] w-full max-w-md p-6 m-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-display font-bold text-lg text-[#1A1A1A]">Reimposta codice — @{resetingFor.username}</h4>
              <button onClick={() => setResetingFor(null)} className="p-1.5 rounded-full hover:bg-[#F9FAFB]"><X className="w-4 h-4" /></button>
            </div>
            <input data-testid="reset-code-input" autoFocus value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="Nuovo codice (min 6 caratteri)" className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm font-mono" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setResetingFor(null)} className="px-4 py-2 text-sm rounded-full border border-[#E5E7EB]">Annulla</button>
              <button data-testid="reset-code-confirm" onClick={submitReset} className="px-5 py-2 text-sm rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white">Conferma</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
